import { RefreshCw, Wrench } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { AdminConfig, AdminHealth } from "@/lib/types"
import { formatDateTime } from "@/lib/utils"

export function AdminOpsPage() {
  const { locale } = useLocale()
  const [config, setConfig] = React.useState<AdminConfig | null>(null)
  const [health, setHealth] = React.useState<AdminHealth | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState<"refresh-tools" | "sync-plugins" | null>(null)

  const load = React.useCallback(() => {
    setLoading(true)
    Promise.all([api.adminConfig(), api.adminHealth()])
      .then(([c, h]) => {
        setConfig(c)
        setHealth(h)
      })
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load ops data."),
      )
      .finally(() => setLoading(false))
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  async function runRefreshTools() {
    if (!confirm("Re-discover every enabled connection's tool catalog now?")) return
    setBusy("refresh-tools")
    try {
      const result = await api.adminRefreshTools()
      const failed = result.failed_slugs.length
        ? `; failed: ${result.failed_slugs.join(", ")}`
        : ""
      toast.success(
        `Checked ${result.connections_checked} connection(s): ` +
          `+${result.tools_added} ~${result.tools_updated} -${result.tools_removed} tools${failed}`,
      )
      load()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Refresh failed.")
    } finally {
      setBusy(null)
    }
  }

  async function runSyncPlugins() {
    if (!confirm("Sync the repo's plugin directory into the catalog now?")) return
    setBusy("sync-plugins")
    try {
      const result = await api.adminSyncPlugins()
      toast.success(`${result.active_plugins} plugin(s) active.`)
      load()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Sync failed.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        title="Config / Ops"
        description="Server configuration, worker health, and catalog maintenance."
        action={
          <Button asChild variant="outline">
            <Link to="/admin">Back to admin</Link>
          </Button>
        }
      />
      <div className="space-y-6 p-6">
        {loading || !config || !health ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <>
            <div>
              <h2 className="mb-3 text-sm font-semibold">Providers</h2>
              {config.providers.length === 0 ? (
                <p className="text-muted-foreground text-sm">No providers configured.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {config.providers.map((provider) => (
                    <Card key={provider.provider_key}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">{provider.label}</CardTitle>
                      </CardHeader>
                      <CardContent className="text-muted-foreground space-y-1 text-xs">
                        <div>{provider.models.join(", ") || "any model"}</div>
                        <div className="flex items-center gap-2">
                          <Badge variant={provider.ready ? "success" : "outline"}>
                            {provider.ready ? "ready" : "not configured"}
                          </Badge>
                          {provider.provider_key === config.default_provider_key && (
                            <Badge variant="secondary">default</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="mb-3 text-sm font-semibold">Feature flags</h2>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant={config.allow_registration ? "success" : "outline"}>
                  registration {config.allow_registration ? "open" : "closed"}
                </Badge>
                <Badge variant={config.memory_extraction_enabled ? "success" : "outline"}>
                  memory extraction {config.memory_extraction_enabled ? "on" : "off"}
                </Badge>
                <Badge variant={config.google_oauth_available ? "success" : "outline"}>
                  Google OAuth {config.google_oauth_available ? "available" : "unavailable"}
                </Badge>
              </div>
            </div>

            <div>
              <h2 className="mb-3 text-sm font-semibold">Worker health</h2>
              <Card>
                <CardContent className="grid gap-2 p-4 text-sm sm:grid-cols-2">
                  <div className="flex items-center gap-2">
                    Database <Badge variant="success">{health.database}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    Scheduler <Badge variant="outline">{health.scheduler.mode}</Badge>
                  </div>
                  <div>Due schedules: {health.scheduler.due_schedules}</div>
                  <div>Stale runs: {health.scheduler.stale_runs}</div>
                  <div className="text-muted-foreground">
                    Last run started: {formatDateTime(health.scheduler.last_run_started_at, locale)}
                  </div>
                  <div className="text-muted-foreground">
                    Last heartbeat: {formatDateTime(health.scheduler.last_heartbeat_at, locale)}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <h2 className="mb-3 text-sm font-semibold">Operations</h2>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" disabled={busy !== null} onClick={runRefreshTools}>
                  <RefreshCw className="size-4" />
                  Refresh tools
                </Button>
                <Button variant="outline" disabled={busy !== null} onClick={runSyncPlugins}>
                  <Wrench className="size-4" />
                  Sync plugins
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
